import React from 'react';
import {
  Badge,
  Button,
  Caption1,
  Dialog,
  DialogActions,
  DialogBody,
  DialogContent,
  DialogSurface,
  DialogTitle,
  MessageBar,
  MessageBarBody,
  MessageBarTitle,
  Text,
  Title2,
  makeStyles,
  tokens,
} from '@fluentui/react-components';
import { Delete20Regular, Edit20Regular } from '@fluentui/react-icons';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ExpandableTable } from '../components/ExpandableTable';
import type { Column } from '../components/ExpandableTable';
import ManagedIdentityEditor from '../components/ManagedIdentityEditor';
import {
  type ManagedIdentityEditorRecord,
  toManagedIdentityEditorRecord,
} from '../utils/managedIdentityEditor';
import { useMsalAuth } from '../hooks/useMsalAuth';
import { createTmdsClient } from '../services/tmds';
import { getErrorMessage } from '../utils/errors';

type IdTokenClaims = {
  preferred_username?: string;
  email?: string;
  upn?: string;
  [key: string]: unknown;
};

interface EditorState {
  mode: 'create' | 'edit';
  identity: ManagedIdentityEditorRecord | null;
}

const useStyles = makeStyles({
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: tokens.spacingVerticalS,
  },
  detail: {
    display: 'grid',
    gridTemplateColumns: '120px 1fr',
    gap: tokens.spacingVerticalXS,
    alignItems: 'start',
  },
  label: {
    color: tokens.colorNeutralForeground3,
  },
});

export default function ManageMSI() {
  const styles = useStyles();
  const { account, getToken } = useMsalAuth();
  const queryClient = useQueryClient();
  const client = React.useMemo(() => createTmdsClient(getToken), [getToken]);
  const callerEmail = React.useMemo(() => {
    const claims = (account?.idTokenClaims as IdTokenClaims | undefined) ?? {};
    const candidate = (
      (typeof claims.preferred_username === 'string' ? claims.preferred_username : '')
      || (typeof claims.email === 'string' ? claims.email : '')
      || (typeof claims.upn === 'string' ? claims.upn : '')
      || (typeof account?.username === 'string' ? account.username : '')
    ).trim().toLowerCase();
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(candidate) ? candidate : '';
  }, [account]);

  const [editor, setEditor] = React.useState<EditorState | null>(null);
  const [deleteTarget, setDeleteTarget] = React.useState<ManagedIdentityEditorRecord | null>(null);
  const [syncNotice, setSyncNotice] = React.useState(false);

  const accessQuery = useQuery({
    queryKey: ['myaccess-for-msi'],
    queryFn: () => client.getMyAccess<unknown>(),
  });
  const sortedPaths = React.useMemo(() => {
    const data = accessQuery.data;
    const list: unknown[] | null = Array.isArray(data)
      ? data
      : typeof data === 'string'
        ? (() => {
            try {
              const parsed: unknown = JSON.parse(data);
              return Array.isArray(parsed) ? parsed : null;
            } catch {
              return null;
            }
          })()
        : null;
    if (!list) return [];
    return Array.from(new Set(list
      .map(value => {
        if (!value || typeof value !== 'object') return '';
        const record = value as Record<string, unknown>;
        const apiPath = record.apiPath ?? record.url_suffix;
        return typeof apiPath === 'string' ? apiPath.trim() : '';
      })
      .filter(Boolean)))
      .sort((left, right) => left.localeCompare(right));
  }, [accessQuery.data]);

  const identityQuery = useQuery({
    queryKey: ['managemsi-all'],
    queryFn: () => client.getManagedIdentities(),
    staleTime: 60_000,
    retry: 3,
    refetchOnMount: 'always',
  });

  const parsedIdentityRecords = React.useMemo(() => {
    const parsed = (identityQuery.data ?? []).map(record => toManagedIdentityEditorRecord(record));
    const items = parsed.filter(
      (record): record is ManagedIdentityEditorRecord => record !== null && !!record.apiPath,
    );
    return { items, droppedCount: parsed.length - items.length };
  }, [identityQuery.data]);

  const deleteIdentity = useMutation({
    mutationFn: async (target: { id: string; apiPath: string }) =>
      client.deleteMSI({ apiPath: target.apiPath, id: target.id }),
    onSuccess: async () => {
      setDeleteTarget(null);
      setSyncNotice(true);
      await queryClient.invalidateQueries({ queryKey: ['managemsi-all'] });
    },
    onError: (error: unknown) => {
      console.error('Delete identity failed:', getErrorMessage(error));
    },
  });

  const columns = React.useMemo<Column<ManagedIdentityEditorRecord>[]>(() => [
    {
      key: 'name',
      label: 'Identity Name',
      render: record => <Text weight="semibold">{record.name || '(unnamed)'}</Text>,
      getValue: record => record.name,
      sortable: true,
      width: '18%',
    },
    {
      key: 'id',
      label: 'Object ID',
      render: record => <Caption1 style={{ fontFamily: 'monospace' }}>{record.id || '-'}</Caption1>,
      getValue: record => record.id,
      sortable: true,
      width: '22%',
    },
    {
      key: 'apiPath',
      label: 'API Path',
      render: record => <Badge appearance="outline">{record.apiPath}</Badge>,
      getValue: record => record.apiPath,
      filterable: true,
      sortable: true,
      width: '22%',
    },
    {
      key: 'owners',
      label: 'Owner(s)',
      render: record => <Text>{record.ownerDisplayTexts.join(', ') || '-'}</Text>,
      getValue: record => record.ownerDisplayTexts.join(', '),
      sortable: true,
      width: '22%',
    },
    {
      key: 'expiry',
      label: 'Expiry',
      render: record => <Caption1>{record.endDate || '-'}</Caption1>,
      getValue: record => record.endDate,
      sortable: true,
    },
  ], []);

  const mutationsDisabled = deleteIdentity.isPending;
  const renderExpanded = (record: ManagedIdentityEditorRecord) => (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
      <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, alignItems: 'center' }}>
        <Button
          size="small"
          appearance="subtle"
          icon={<Edit20Regular />}
          onClick={event => {
            event.stopPropagation();
            setEditor({ mode: 'edit', identity: record });
          }}
          disabled={mutationsDisabled}
        >
          Edit
        </Button>
        <Button
          size="small"
          appearance="outline"
          icon={<Delete20Regular />}
          style={{ color: tokens.colorPaletteRedForeground1, borderColor: tokens.colorPaletteRedForeground1 }}
          onClick={event => {
            event.stopPropagation();
            if (record.id) setDeleteTarget(record);
          }}
          disabled={mutationsDisabled || !record.id}
        >
          Delete
        </Button>
      </div>
      <div className={styles.detail}>
        <Text className={styles.label}>Object ID</Text><code>{record.id || '-'}</code>
        <Text className={styles.label}>Name</Text><Text>{record.name || '-'}</Text>
        <Text className={styles.label}>API Path</Text><Badge appearance="outline">{record.apiPath}</Badge>
        <Text className={styles.label}>Start Date</Text><Text>{record.startDate || '-'}</Text>
        <Text className={styles.label}>End Date</Text><Text>{record.endDate || '-'}</Text>
        <Text className={styles.label}>Owners</Text>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          {record.ownerDisplayTexts.length
            ? record.ownerDisplayTexts.map((text, index) => (
                <Text key={`${text}-${index}`} weight={text === callerEmail ? 'semibold' : 'regular'}>{text}</Text>
              ))
            : <Caption1>No owners</Caption1>}
          {record.unresolvedCount > 0 && (
            <Caption1>
              {record.unresolvedCount} owner label(s) could not be resolved to a display name;
              the canonical owner(s) are preserved.
            </Caption1>
          )}
        </div>
      </div>
    </div>
  );

  return (
    <div style={{ padding: 16 }}>
      <div className={styles.header}>
        <Title2>Manage Identities</Title2>
        <Button
          appearance="primary"
          onClick={() => setEditor({ mode: 'create', identity: null })}
          disabled={accessQuery.isLoading || sortedPaths.length === 0 || mutationsDisabled}
        >
          Add Identity
        </Button>
      </div>

      <Caption1>Showing managed identities across all accessible API paths.</Caption1>

      <MessageBar intent="info" style={{ marginTop: 12, marginBottom: 4 }}>
        <MessageBarBody>
          Identity changes sync to the gateway at the top of each hour.
          Newly added or modified identities will not be active until the next hourly sync.
        </MessageBarBody>
      </MessageBar>

      {syncNotice && (
        <MessageBar intent="warning" style={{ marginTop: 4, marginBottom: 4 }}>
          <MessageBarBody>
            <MessageBarTitle>Saved</MessageBarTitle>
            Your changes have been saved but won't take effect until the next hourly sync (top of the hour).
          </MessageBarBody>
        </MessageBar>
      )}

      {identityQuery.isError && (
        <MessageBar intent="error" style={{ marginTop: 12, marginBottom: 12 }}>
          <MessageBarBody>
            <MessageBarTitle>Error</MessageBarTitle>
            {getErrorMessage(identityQuery.error)}
          </MessageBarBody>
        </MessageBar>
      )}

      {parsedIdentityRecords.droppedCount > 0 && (
        <MessageBar intent="warning" style={{ marginTop: 12, marginBottom: 12 }}>
          <MessageBarBody>
            <MessageBarTitle>Skipped invalid managed identity rows</MessageBarTitle>
            {parsedIdentityRecords.droppedCount} row(s) were missing apiPath and cannot be edited or deleted here.
          </MessageBarBody>
        </MessageBar>
      )}

      <ExpandableTable<ManagedIdentityEditorRecord>
        columns={columns}
        items={parsedIdentityRecords.items}
        getRowKey={record => `${record.apiPath}:${record.id || record.name}`}
        renderExpanded={renderExpanded}
        loading={identityQuery.isLoading}
        emptyMessage="No managed identities found."
        searchPlaceholder="Search by name, ID, path, or owner..."
        defaultSortKey="apiPath"
      />

      <ManagedIdentityEditor
        open={editor !== null}
        mode={editor?.mode ?? 'create'}
        identity={editor?.identity}
        fixedApiPath={editor?.mode === 'edit' ? editor.identity?.apiPath : undefined}
        apiPathOptions={sortedPaths}
        client={client}
        callerEmail={callerEmail}
        onClose={() => setEditor(null)}
        onSaved={async () => {
          setSyncNotice(true);
          await queryClient.invalidateQueries({ queryKey: ['managemsi-all'] });
        }}
      />

      <Dialog open={deleteTarget !== null} onOpenChange={(_, data) => { if (!data.open) setDeleteTarget(null); }}>
        <DialogSurface>
          <DialogBody>
            <DialogTitle>Confirm Identity Delete</DialogTitle>
            <DialogContent>
              {deleteIdentity.isError && (
                <Text role="alert" style={{ color: tokens.colorPaletteRedForeground1 }}>
                  {getErrorMessage(deleteIdentity.error)}
                </Text>
              )}
              <Text>
                Delete identity{deleteTarget?.name ? ` "${deleteTarget.name}"` : ''} from
                {' '}<strong>{deleteTarget?.apiPath}</strong>? This may remove all owners.
              </Text>
            </DialogContent>
            <DialogActions>
              <Button appearance="secondary" onClick={() => setDeleteTarget(null)} disabled={deleteIdentity.isPending}>Cancel</Button>
              <Button
                appearance="primary"
                onClick={() => deleteTarget?.id && deleteTarget.apiPath
                  && deleteIdentity.mutate({ id: deleteTarget.id, apiPath: deleteTarget.apiPath })}
                disabled={mutationsDisabled || !deleteTarget?.id}
                style={{ color: tokens.colorPaletteRedForeground1 }}
              >
                {deleteIdentity.isPending ? 'Deleting...' : 'Delete'}
              </Button>
            </DialogActions>
          </DialogBody>
        </DialogSurface>
      </Dialog>
    </div>
  );
}
